import { Box, Grid, Link, Typography } from '@mui/material';
import { createSelector } from '@reduxjs/toolkit';
import { useFormik } from 'formik';
import { useMemo, useState, FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';

import { MissingControlPersonErrorMessage } from './components';
import { useGetLegalDocumentsQuery } from '../../../../api/legal-documents';
import { LegalDocumentKey } from '../../../../domain/config';
import { isSmallBusiness } from '../../../../domain/qualification';
import { AddressView } from '../../../../general/components/address-view';
import { DetailsAccordion } from '../../../../general/components/details-accordion';
import { YouCanComeBackNote } from '../../../../general/components/footers/you-can-come-back-note';
import { RequiredText } from '../../../../general/components/required-text';
import { WizardPage } from '../../../../general/components/wizard-page';
import { FormikToggle } from '../../../../general/inputs/formik-toggle';
import { formatCentsCurrency } from '../../../../general/utils/formatters';
import { selectBusinessInfo } from '../../../application/reducers/business-info';
import { selectCreditTerms } from '../../../application/reducers/credit-terms.reducer';
import { nextStep, selectnextStep } from '../../../application/reducers/next-step';
import { selectPersonalInfo, updatePersonalInfoOwnershipAndControl } from '../../../application/reducers/personalInfo';
import { recordConsent, selectConsent } from '../../../legal-consent/reducers/consent';
import { cardProgramTermsSelector } from '../../../legal-consent/selectors/legal-document-selectors';
import { SubmitWorkflowButton } from '../../../workflow/components/submit-workflow-button';
import { OtherIndividuals } from '../../components/other-individuals';
import { DetailsRow, ReviewDetailsGrid } from '../../components/review-grid';
import { selectOtherIndividuals } from '../../reducers/other-individuals.reducer';

const selector = createSelector(
  [
    selectnextStep,
    selectCreditTerms,
    selectOtherIndividuals,
    cardProgramTermsSelector,
    selectPersonalInfo,
    selectBusinessInfo,
    selectConsent,
  ],
  (
    nextStep,
    { creditTerms },
    otherIndividuals,
    cardProgramTerms,
    personalInfo,
    { businessInfo: business },
    consent,
  ) => ({
    creditLimit: creditTerms?.finalCreditLimit,
    personalInfo,
    business,
    cardProgramTerms,
    saving: consent.loading || nextStep.loading,
    otherIndividuals,
    loading: nextStep.loading,
  }),
);

const consentCardTermsSchema = yup.mixed().oneOf([true], 'You must read and accept the Card Program Terms');
const consentOfferSummarySchema = yup.mixed().oneOf([true], 'You must read and accept the Coast Offer Summary');

const validationSchemaWithOfferSummary = yup.object({
  consentCardTerms: consentCardTermsSchema,
  consenstOfferSummary: consentOfferSummarySchema,
});

const validationSchemaCardTermsOnly = yup.object({
  consentCardTerms: consentCardTermsSchema,
});

const ReviewApplicationPage: FC = () => {
  const [isControlPersonError, setIsControlPersonError] = useState(false);
  const dispatch = useDispatch();
  const { creditLimit, personalInfo, business, cardProgramTerms, saving, otherIndividuals, loading } =
    useSelector(selector);

  const { data, isFetching: isFetchingLegalDocs } = useGetLegalDocumentsQuery();
  const commercialFinancingDisclosure = data?.entities.commercial_financing_disclosure;

  const isOtherIndividualControlPerson = useMemo(() => {
    return otherIndividuals.some((individual) => individual.controlPerson);
  }, [otherIndividuals]);
  const isApplicantControlPerson = personalInfo.controlPerson;

  const formik = useFormik({
    initialValues: {
      personalInfo: {
        ...personalInfo,
      },
      consentCardTerms: false,
      consenstOfferSummary: false,
    },
    validationSchema: commercialFinancingDisclosure ? validationSchemaWithOfferSummary : validationSchemaCardTermsOnly,
    validate: (values) => {
      const hasControlPerson =
        Boolean(values.personalInfo.controlPerson) || isApplicantControlPerson || isOtherIndividualControlPerson;
      const errors: Record<string, boolean> = {};

      if (!hasControlPerson) {
        setIsControlPersonError(true);
        errors.missingControlPerson = true;
      } else {
        setIsControlPersonError(false);
        delete errors.missingControlPerson;
      }

      return errors;
    },
    validateOnChange: false,
    onSubmit: (values) => {
      const documentKeys: LegalDocumentKey[] = commercialFinancingDisclosure
        ? ['card_program_terms', 'commercial_financing_disclosure']
        : ['card_program_terms'];
      dispatch(
        recordConsent({
          documentKeys,
          onSuccess: [
            updatePersonalInfoOwnershipAndControl({
              beneficialOwner: values.personalInfo.beneficialOwner,
              controlPerson: values.personalInfo.controlPerson,
            }),
            nextStep({
              action: 'get_ubos',
              gotoNextPage: true,
            }),
          ],
        }),
      );
    },
  });

  const businessDetails: DetailsRow[] = useMemo(() => {
    return [
      { label: 'Business Name', value: business.name },
      { label: 'Type', value: business.businessType },
      {
        label: 'Address',
        value: <AddressView address={business.address} isPublic />,
      },
      {
        label: 'Annual Business Revenue',
        value: formatCentsCurrency(business.annualRevenue ?? 0),
      },
      {
        label: 'Monthly Fuel Spend',
        value: formatCentsCurrency(business.monthlyFuelSpend ?? 0),
      },
      {
        label: 'Fleet Size',
        value: business.fleetSize,
      },
    ];
  }, [business]);

  const smallBusiness = isSmallBusiness(business);

  return (
    !loading &&
    !isFetchingLegalDocs &&
    creditLimit && (
      <WizardPage
        title={
          <>
            Review your application for a <br />
            <Typography component="span" color="primary" variant="inherit">
              {formatCentsCurrency(creditLimit)}
            </Typography>{' '}
            credit limit
          </>
        }
        footer={<YouCanComeBackNote />}
      >
        <form onSubmit={formik.handleSubmit}>
          <Grid container direction="column" spacing={4}>
            <Grid item>
              <OtherIndividuals showApplicant={smallBusiness} includePhone={true} />
            </Grid>
            <Grid item>
              <DetailsAccordion
                title="Business"
                details={<ReviewDetailsGrid details={businessDetails} label="Business" />}
              />
            </Grid>
            <Grid item>
              <FormikToggle
                formik={formik}
                id="consentCardTerms"
                name="consentCardTerms"
                label={
                  <RequiredText>
                    I agree to the Coast{' '}
                    <Link href={cardProgramTerms?.url} target="_blank">
                      Card Program Terms
                    </Link>
                  </RequiredText>
                }
                toggleStyle="checkbox"
              />
              {commercialFinancingDisclosure && (
                <FormikToggle
                  formik={formik}
                  id="consenstOfferSummary"
                  name="consenstOfferSummary"
                  label={
                    <RequiredText>
                      I have reviewed and acknowledge receipt of the{' '}
                      <Link href={commercialFinancingDisclosure.url} target="_blank">
                        Coast Offer Summary
                      </Link>
                    </RequiredText>
                  }
                  toggleStyle="checkbox"
                />
              )}

              <Box>
                <Typography variant="body2">
                  By submitting, I attest that the information provided is accurate to the best of my knowledge.
                </Typography>
              </Box>
              {isControlPersonError && (
                <Box mt={2}>
                  <MissingControlPersonErrorMessage />
                </Box>
              )}
              <SubmitWorkflowButton type="submit" progress={saving} sx={{ mt: 4 }}>
                Submit
              </SubmitWorkflowButton>
            </Grid>
          </Grid>
        </form>
      </WizardPage>
    )
  );
};

export { ReviewApplicationPage };
